import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { SlidersHorizontal, SearchX, X, ChevronLeft, ChevronRight } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { api } from "../lib/api";
import ProductCard from "../components/ProductCard";

const PRICE_BUCKETS = [
  { label: "Under ₹500", min: 0, max: 500 },
  { label: "₹500 – ₹1000", min: 500, max: 1000 },
  { label: "₹1000 – ₹2000", min: 1000, max: 2000 },
  { label: "₹2000 – ₹5000", min: 2000, max: 5000 },
  { label: "Above ₹5000", min: 5000, max: null },
];

const SORTS = [
  ["recommended", "Recommended"], ["popular", "Popular"], ["price_asc", "Price: Low to High"],
  ["price_desc", "Price: High to Low"], ["rating", "Highest Rated"], ["newest", "New Arrivals"], ["discount", "Biggest Discount"],
];

const Skeleton = () => (
  <div className="bg-white rounded-2xl border border-[#E9E2D7] overflow-hidden animate-pulse">
    <div className="aspect-[4/5] bg-[#F5EFE6]" />
    <div className="p-4 space-y-2"><div className="h-3 bg-[#F5EFE6] rounded w-3/4" /><div className="h-3 bg-[#F5EFE6] rounded w-1/2" /><div className="h-9 bg-[#F5EFE6] rounded-full mt-3" /></div>
  </div>
);

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const [meta, setMeta] = useState({ categories: [], occasions: [] });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const q = params.get("q") || "";
  const category = params.get("category") || "";
  const occasion = params.get("occasion") || "";
  const delivery = params.get("delivery") || "";
  const tag = params.get("tag") || "";
  const sort = params.get("sort") || "recommended";
  const priceIdx = params.get("price") ?? "";
  const rating = params.get("rating") || "";
  const discount = params.get("discount") || "";
  const inStock = params.get("stock") === "1";
  const page = parseInt(params.get("page") || "1");

  const setParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (value === "" || value === null) next.delete(key);
    else next.set(key, value);
    if (key !== "page") next.delete("page");
    setParams(next);
  };

  useEffect(() => {
    api.get("/meta").then(({ data }) => setMeta(data)).catch(() => {});
  }, []);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const bucket = priceIdx !== "" ? PRICE_BUCKETS[parseInt(priceIdx)] : null;
      const { data } = await api.get("/products", {
        params: {
          q: q || undefined, category: category || undefined, occasion: occasion || undefined,
          delivery: delivery || undefined, tag: tag || undefined, sort, page, limit: 12,
          min_price: bucket?.min ?? undefined, max_price: bucket?.max ?? undefined,
          min_rating: rating || undefined, min_discount: discount || undefined,
          in_stock: inStock || undefined,
        },
      });
      setData(data);
    } catch {
      setData({ items: [], total: 0, pages: 1 });
    } finally {
      setLoading(false);
    }
  }, [q, category, occasion, delivery, tag, sort, priceIdx, rating, discount, inStock, page]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const clearAll = () => setParams(new URLSearchParams());
  const hasFilters = q || category || occasion || delivery || tag || priceIdx !== "" || rating || discount || inStock;

  const heading = q
    ? `Results for "${q}"`
    : meta.categories.find((c) => c.slug === category)?.name
    || meta.occasions.find((o) => o.slug === occasion)?.name
    || (delivery === "same_day" ? "Same Day Delivery Gifts" : tag === "bestseller" ? "Best Sellers" : "All Gifts");

  const FilterPanel = (
    <div className="space-y-7" data-testid="filter-panel">
      <div>
        <h4 className="text-xs font-bold uppercase tracking-[0.18em] text-stone-500 mb-3">Category</h4>
        <div className="space-y-1.5">
          {meta.categories.map((c) => (
            <button key={c.slug} onClick={() => setParam("category", category === c.slug ? "" : c.slug)} className={`block w-full text-left px-3 py-2 rounded-xl text-sm transition-colors ${category === c.slug ? "bg-[#B45309]/10 text-[#B45309] font-bold" : "text-stone-600 hover:bg-[#F5EFE6]"}`} data-testid={`filter-category-${c.slug}`}>
              {c.name}
            </button>
          ))}
        </div>
      </div>
      <div>
        <h4 className="text-xs font-bold uppercase tracking-[0.18em] text-stone-500 mb-3">Occasion</h4>
        <div className="space-y-1.5 max-h-52 overflow-y-auto pr-1">
          {meta.occasions.map((o) => (
            <button key={o.slug} onClick={() => setParam("occasion", occasion === o.slug ? "" : o.slug)} className={`block w-full text-left px-3 py-2 rounded-xl text-sm transition-colors ${occasion === o.slug ? "bg-[#B45309]/10 text-[#B45309] font-bold" : "text-stone-600 hover:bg-[#F5EFE6]"}`} data-testid={`filter-occasion-${o.slug}`}>
              {o.name}
            </button>
          ))}
        </div>
      </div>
      <div>
        <h4 className="text-xs font-bold uppercase tracking-[0.18em] text-stone-500 mb-3">Price</h4>
        <div className="space-y-1.5">
          {PRICE_BUCKETS.map((b, i) => (
            <label key={b.label} className="flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm text-stone-600 hover:bg-[#F5EFE6] cursor-pointer transition-colors">
              <input type="radio" name="price" checked={priceIdx === String(i)} onChange={() => setParam("price", String(i))} className="accent-[#B45309]" data-testid={`filter-price-${i}`} />
              {b.label}
            </label>
          ))}
        </div>
      </div>
      <div>
        <h4 className="text-xs font-bold uppercase tracking-[0.18em] text-stone-500 mb-3">Rating</h4>
        {[["4", "4★ & above"], ["3", "3★ & above"]].map(([v, label]) => (
          <label key={v} className="flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm text-stone-600 hover:bg-[#F5EFE6] cursor-pointer transition-colors">
            <input type="radio" name="rating" checked={rating === v} onChange={() => setParam("rating", rating === v ? "" : v)} className="accent-[#B45309]" data-testid={`filter-rating-${v}`} />
            {label}
          </label>
        ))}
      </div>
      <div>
        <h4 className="text-xs font-bold uppercase tracking-[0.18em] text-stone-500 mb-3">Discount</h4>
        {[["10", "10% & above"], ["25", "25% & above"]].map(([v, label]) => (
          <label key={v} className="flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm text-stone-600 hover:bg-[#F5EFE6] cursor-pointer transition-colors">
            <input type="radio" name="discount" checked={discount === v} onChange={() => setParam("discount", discount === v ? "" : v)} className="accent-[#B45309]" data-testid={`filter-discount-${v}`} />
            {label}
          </label>
        ))}
      </div>
      <div>
        <h4 className="text-xs font-bold uppercase tracking-[0.18em] text-stone-500 mb-3">Delivery</h4>
        {[["same_day", "Same Day"], ["express", "Express"], ["standard", "Standard"]].map(([v, label]) => (
          <label key={v} className="flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm text-stone-600 hover:bg-[#F5EFE6] cursor-pointer transition-colors">
            <input type="radio" name="delivery" checked={delivery === v} onChange={() => setParam("delivery", delivery === v ? "" : v)} className="accent-[#B45309]" data-testid={`filter-delivery-${v}`} />
            {label}
          </label>
        ))}
        <label className="flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm text-stone-600 hover:bg-[#F5EFE6] cursor-pointer transition-colors mt-2">
          <input type="checkbox" checked={inStock} onChange={(e) => setParam("stock", e.target.checked ? "1" : "")} className="accent-[#B45309]" data-testid="filter-instock" />
          In stock only
        </label>
      </div>
      {hasFilters && (
        <button onClick={clearAll} className="w-full h-10 rounded-full border border-stone-300 text-sm font-semibold text-stone-600 hover:border-[#B45309] hover:text-[#B45309] transition-colors" data-testid="clear-filters">
          Clear all filters
        </button>
      )}
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-10 lg:py-14" data-testid="shop-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#B45309]">Our Collection</p>
          <h1 className="font-serif text-3xl sm:text-4xl font-bold text-stone-900 mt-2 tracking-tight">{heading}</h1>
          {data && <p className="text-sm text-stone-500 mt-1">{data.total} gift{data.total !== 1 ? "s" : ""} found</p>}
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => setFiltersOpen(true)} className="lg:hidden inline-flex items-center gap-2 h-10 px-4 rounded-full border border-stone-300 text-sm font-semibold" data-testid="filters-open-mobile">
            <SlidersHorizontal size={15} /> Filters
          </button>
          <select value={sort} onChange={(e) => setParam("sort", e.target.value)} className="h-10 px-4 rounded-full border border-stone-300 bg-white text-sm font-medium outline-none focus:border-[#B45309]" data-testid="sort-select">
            {SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-10">
        <aside className="hidden lg:block"><div className="sticky top-28">{FilterPanel}</div></aside>

        <div>
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} />)}</div>
          ) : data?.items.length ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="product-grid">
                {data.items.map((p) => <ProductCard key={p.id} product={p} />)}
              </div>
              {data.pages > 1 && (
                <div className="flex items-center justify-center gap-3 mt-12" data-testid="pagination">
                  <button disabled={page <= 1} onClick={() => setParam("page", String(page - 1))} className="w-10 h-10 rounded-full border border-stone-300 grid place-items-center disabled:opacity-40 hover:border-[#B45309] transition-colors" aria-label="Previous page" data-testid="page-prev"><ChevronLeft size={16} /></button>
                  <span className="text-sm font-semibold text-stone-600">Page {page} of {data.pages}</span>
                  <button disabled={page >= data.pages} onClick={() => setParam("page", String(page + 1))} className="w-10 h-10 rounded-full border border-stone-300 grid place-items-center disabled:opacity-40 hover:border-[#B45309] transition-colors" aria-label="Next page" data-testid="page-next"><ChevronRight size={16} /></button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-20 rounded-3xl bg-white border border-[#E9E2D7]" data-testid="no-results">
              <SearchX className="mx-auto text-stone-300" size={44} />
              <h3 className="font-serif text-2xl font-semibold text-stone-800 mt-5">We couldn't find that gift.</h3>
              <p className="text-stone-500 mt-2">Try a different word — or explore these popular categories.</p>
              <div className="flex flex-wrap justify-center gap-2 mt-6">
                {meta.categories.slice(0, 5).map((c) => (
                  <button key={c.slug} onClick={() => { clearAll(); setParams({ category: c.slug }); }} className="px-4 py-2 rounded-full bg-[#F5EFE6] text-sm font-semibold text-stone-700 hover:bg-[#B45309] hover:text-white transition-colors" data-testid={`empty-category-${c.slug}`}>
                    {c.name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <AnimatePresence>
        {filtersOpen && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[70] bg-stone-900/50 lg:hidden" onClick={() => setFiltersOpen(false)}>
            <motion.div initial={{ x: "-100%" }} animate={{ x: 0 }} exit={{ x: "-100%" }} transition={{ type: "tween", duration: 0.25 }} className="absolute left-0 top-0 bottom-0 w-[85%] max-w-sm bg-[#FDFBF7] p-6 overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="mobile-filter-drawer">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-serif text-xl font-bold">Filters</h3>
                <button onClick={() => setFiltersOpen(false)} aria-label="Close filters" data-testid="filters-close-mobile"><X size={20} /></button>
              </div>
              {FilterPanel}
              <button onClick={() => setFiltersOpen(false)} className="mt-6 w-full h-11 rounded-full bg-[#B45309] text-white text-sm font-bold" data-testid="filters-apply-mobile">Show results</button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
